import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import { useFormikContext } from "formik"
import { Box, Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import {
    CreateUpdateBooking,
    CreateUpdateBookingOrganizer,
    ResourceFiltersTypes,
    SelectedMainUserState,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
} from "../../createOrderTypes"
import { CreateUpdateBookingFormValues } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import { bookingFormValuesToDynamicConfigKey, generateConfirmBookingInitialValues } from "../form/helpers"
import { WeeklyResourceBookingCalendar } from "./resourceBookingCalendars/WeeklyResourceBookingCalendar"
import { NullableCalendarEvent, TimelineCalendarResource } from "./calendar/fullCalendarTypes"
import { ResourceBookingCalendar } from "./resourceBookingCalendars/ResourceBookingCalendar"
import { BookingViewMenu, ViewType } from "./resourceBookingCalendars/BookingViewMenu"
import { CreateUpdateBookingDialogWrap } from "./CreateUpdateBookingDialogWrap"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { OrderType } from "../../../../../modules/payments/orderTypes"
import { ResourceFilters } from "./resourceFilters/ResourceFilters"
import { ResourceBookingControls } from "./ResourceBookingControls"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { LoadingSpinner } from "../../../loading/LoadingSpinner"
import { InitialEventState } from "./useInitialEventState"
import { buildFinalResources, filterResources } from "./helpers"
import {
    BookingConfigurationResponse,
    ConfigQuery,
    createBooking,
    CreateUpdateBookingResponse,
    fetchBookingConfiguration,
    fetchTimelineResources,
    TimelineResourcesResponse,
    updateBooking,
} from "../../createOrderApi"
import { OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { DynamicMainFormValues } from "../form/formTypes"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { mergeValuesWithConfig } from "../form/mergeValuesWithConfig"
import { OrganizerType } from "../../../../types/OrganizerTypes"

const useStyles = makeStyles()((theme) => ({
    calendarWrapper: {
        position: "relative",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    headerWrap: {
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    button: {
        color: theme.palette.tailwind.slate[800],
        padding: ".5rem 1rem",
        flex: "none",
    },
}))

const selectBookingConfig = (data: BookingConfigurationResponse) => data?.bookingConfig

const transformData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) {
        return { calendarResources: [], groupImagesMap: new Map<string, string>() }
    }

    const groupImagesMap = new Map<string, string>()
    const calendarResources: TimelineCalendarResource[] = []

    serviceCentersData.forEach((group) => {
        if (group.floorPlan?.imageUrl) {
            groupImagesMap.set(group.name, group.floorPlan.imageUrl)
        }

        group.resources.forEach((resource) => {
            calendarResources.push({
                id: String(resource.id),
                location: group.name,
                locationId: `${String(group.id)}_${String(resource.id)}`,
                serviceCenterUid: group.uid,
                title: resource.displayName || resource.name,
                seats: resource.room?.seats,
                floor: resource.room?.floor?.value,
                avEquipments: resource.room?.avEquipments || [],
                buffer: resource.buffer,
                tableSetups: resource.room?.tableSetups,
                outOfService: resource.outOfService,
                image: resource.image,
                meetingUsage: resource.room?.meetingUsage,
                // hasMessagingEnabled: !!group.messaging,
                type: resource.type,
            })
        })
    })

    return { calendarResources, groupImagesMap }
}

type ResourceBookingProps = Readonly<{
    initialEventState: InitialEventState
    handleCancelClick: () => void
    confirmBookingDialog: boolean
    setConfirmBookingDialog: Dispatch<SetStateAction<boolean>>
    handleOpenSuccessDialog: (orderDetails: CreateUpdateBookingResponse, order: CreateUpdateBooking) => void
    handleClearResourceIds: () => void
    user: SelectedMainUserState
    orderContext: OrderContextState
    refreshEventsToken: number
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderDetails?: MeetingServiceOrder
    flowConfig?: CreateOrderDialogFlow
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    dialogType: CreateOrderDialogType
}>

export function ResourceBooking({
    initialEventState,
    handleCancelClick,
    confirmBookingDialog,
    setConfirmBookingDialog,
    handleOpenSuccessDialog,
    handleClearResourceIds,
    user,
    orderContext,
    refreshEventsToken,
    setOrderContext,
    orderDetails,
    flowConfig,
    orderFormStatusRef,
    dialogType,
}: ResourceBookingProps) {
    const { orderId, mode } = orderContext
    const { isFullInitOrder, meetingServiceId, resourceBookingId } = orderFormStatusRef.current

    const { classes } = useStyles()
    const { values, setFieldValue } = useFormikContext<DynamicMainFormValues>()
    const event: NullableCalendarEvent = values["event"]

    const dateTimeFactory = useDateTime()

    const [selectedView, setSelectedView] = useState(ViewType.TIMELINE)
    const [filters, setFilters] = useState<ResourceFiltersTypes>({
        filters: [
            {
                identifier: "resourceTypeIds",
                values: [
                    { key: "Room", value: 1 },
                    { key: "Av equipment", value: 2 },
                ],
            },
        ],
        search: "",
        isBookedPeriod: false,
    })

    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    const {
        data: { calendarResources, groupImagesMap } = {
            calendarResources: [],
            groupImagesMap: new Map<string, string>(),
        },
        isLoading,
    } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: {
            userId: user.userId,
            resourceTypes: filters.filters
                .find((f) => f.identifier === "resourceTypeIds")
                ?.values.map((v) => String(v.key).trim().split(" ")[0].toUpperCase())
                .join(","),
        },

        select: transformData,
    })

    const { mutateAsync: callCreateBooking } = useApiMutation(createBooking, {
        async onSuccess(params, { order, calendarImportLinks }, context) {
            handleDialogClose()
            await context.queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
            orderFormStatusRef.current.resourceBookingId = order.id
            setOrderContext((prev) => {
                return { ...prev, orderId: order.id }
            })
            handleOpenSuccessDialog({ order, calendarImportLinks }, params.content)
        },
    })

    const { mutateAsync: callUpdateBooking } = useApiMutation(updateBooking, {
        async onSuccess(params, { order, calendarImportLinks }, context) {
            handleDialogClose()
            await context.queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
            order && handleOpenSuccessDialog({ order, calendarImportLinks }, params.content)
        },
    })

    const filteredResources = useMemo(
        () => filterResources(calendarResources, filters.filters, searchRegex, event),
        [calendarResources, filters.filters, searchRegex, event],
    )

    const { data: bookingConfig } = useApiQuery(fetchBookingConfiguration, {
        queryName: ConfigQuery.BOOKING_CONFIG,
        dependencies: { userId: user.userId },
        select: selectBookingConfig,
        enabled: !!event,
        useErrorBoundary: true,
    })

    useEffect(() => {
        if (bookingConfig && event) {
            mergeValuesWithConfig({
                existing: values,
                config: bookingConfig,
                user,
                dateTimeFactory,
                dialogType,
                orderDetails,
                mode,
                flowConfig,
                setFieldValue,
            })
        }
    }, [bookingConfig, event])

    const handleBookClick = useCallback(() => {
        setConfirmBookingDialog(true)
    }, [])

    const handleDialogClose = useCallback(
        (formValues?: CreateUpdateBookingFormValues) => {
            if (formValues) {
                const updatedFields = bookingFormValuesToDynamicConfigKey(formValues, values)
                Object.entries(updatedFields).forEach(([key, value]) => {
                    setFieldValue(key, value)
                })
            }

            setConfirmBookingDialog(false)
        },
        [values],
    )

    const handleSubmit = ({
        meetingDetails,
        resources,
        organizers,
        attendees,
        ...rest
    }: CreateUpdateBookingFormValues) => {
        const finalResources = buildFinalResources(meetingDetails.resourceIds, resources)
        // const organizersList: CreateUpdateBookingOrganizer[] = [
        //     {
        //         id: user.userId,
        //         organizerType: OrganizerType.CREATOR,
        //     },
        // ]

        const organizersList: CreateUpdateBookingOrganizer[] = []

        if (organizers?.id) {
            organizersList.push({
                id: organizers.id,
                organizerType: OrganizerType.ORGANIZER,
            })
        } else if (organizers?.name && organizers.name.trim()) {
            organizersList.push({
                organizerType: OrganizerType.ORGANIZER,
                name: organizers.name.trim(),
            })
        }

        const { resourceIds, startDateTime, endDateTime, ...restMeetingDetails } = meetingDetails

        const modifyValues = {
            orderType: OrderType.MEETING,
            ...rest,
            organizers: organizersList,
            meetingDetails: {
                ...restMeetingDetails,
                startDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(startDateTime)),
                endDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(endDateTime)),
                attendees,
                ...(flowConfig?.calendarAppointmentId && { calendarAppointmentId: flowConfig.calendarAppointmentId }),
            },
            bookingDetails: {
                resources: finalResources,
            },
        }

        if (isFullInitOrder || resourceBookingId) {
            let meetingId = initialEventState.initValues?.id || orderId
            callUpdateBooking({ userId: user.userId, orderId: meetingId ?? 0, content: modifyValues })
        } else {
            callCreateBooking({
                userId: user.userId,
                content: {
                    ...modifyValues,
                    ...(meetingServiceId ? { order: { id: meetingServiceId } } : {}),
                },
            })
        }
    }

    const handleChangeBookingView = (view: ViewType) => {
        selectedView === ViewType.AVAILABLE_TIME && setFieldValue("event", { ...event, timeRange: undefined })
        setSelectedView(view)
        handleCancelClick()
    }

    const handleChangeOrderContext = (id: string) => {
        setOrderContext({ mode: "edit", orderId: +id })
    }

    return (
        <Box className={classes.calendarWrapper}>
            <Grid
                container
                justifyContent="space-between"
                flexWrap="nowrap"
                alignItems="center"
                className={classes.headerWrap}
            >
                <ResourceFilters
                    filters={filters}
                    userId={user.userId}
                    setFilters={setFilters}
                    selectedView={selectedView}
                    isTimeline
                    isSpecialFlow={!!flowConfig?.flow}
                />

                <BookingViewMenu selectedView={selectedView} onChangeView={handleChangeBookingView} />
            </Grid>

            {!isLoading ? (
                <>
                    {selectedView === ViewType.TIMELINE && (
                        <ResourceBookingCalendar
                            timelineResources={filteredResources}
                            searchRegex={searchRegex}
                            groupImagesMap={groupImagesMap}
                            localEventUpdateId={initialEventState.initValues?.id || orderId}
                            refreshEventsToken={refreshEventsToken}
                            userId={user.userId}
                            isBookedPeriod={!!filters.isBookedPeriod}
                            handleChangeOrderContext={handleChangeOrderContext}
                            flowConfig={flowConfig}
                        />
                    )}
                    {selectedView === ViewType.AVAILABLE_TIME && (
                        <WeeklyResourceBookingCalendar
                            timelineResources={filteredResources}
                            searchRegex={searchRegex}
                            groupImagesMap={groupImagesMap}
                            handleBookClick={handleBookClick}
                        />
                    )}
                </>
            ) : (
                <LoadingSpinner />
            )}
            {selectedView === ViewType.TIMELINE && (
                <ResourceBookingControls
                    initialEventState={initialEventState}
                    onCancel={handleCancelClick}
                    onBook={handleBookClick}
                    isLoadingButton={confirmBookingDialog}
                    flow={flowConfig?.flow}
                />
            )}

            {confirmBookingDialog && bookingConfig && event && (
                <CreateUpdateBookingDialogWrap
                    open={confirmBookingDialog}
                    onClose={handleDialogClose}
                    event={event}
                    initialValues={generateConfirmBookingInitialValues(values, initialEventState.initValues?.id)}
                    resourcesList={values["fullResourcesList"]}
                    handleSubmit={handleSubmit}
                    handleClearResourceIds={handleClearResourceIds}
                    user={user}
                    orderId={isFullInitOrder ? orderId : resourceBookingId}
                    bookingConfig={bookingConfig}
                />
            )}
        </Box>
    )
}
